import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash } from "@phosphor-icons/react";

export default function AdminAssignments() {
  const [players, setPlayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [playerId, setPlayerId] = useState("");
  const [coachId, setCoachId] = useState("");
  const [parentId, setParentId] = useState("");
  const [err, setErr] = useState("");

  const refresh = async () => {
    const [p, u, a] = await Promise.all([
      api.get("/players"), api.get("/users"), api.get("/coach-assignments"),
    ]);
    setPlayers(p.data); setUsers(u.data); setAssignments(a.data);
  };
  useEffect(() => { refresh(); }, []);

  const coaches = users.filter((u) => u.role === "coach" || u.role === "head_coach" || u.role === "external_coach");
  const parents = users.filter((u) => u.role === "parent");
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
  const playerMap = Object.fromEntries(players.map((p) => [p.id, p]));

  const addAssign = async () => {
    setErr("");
    if (!playerId || !coachId) return setErr("Pick a player and a coach.");
    try {
      await api.post("/coach-assignments", { player_id: playerId, coach_user_id: coachId, type: "club" });
      setPlayerId(""); setCoachId("");
      refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    }
  };

  const removeAssign = async (id) => {
    await api.delete(`/coach-assignments/${id}`);
    refresh();
  };

  const linkParent = async () => {
    setErr("");
    if (!playerId || !parentId) return setErr("Pick a player and a parent.");
    try {
      await api.post("/player-parents", { player_id: playerId, parent_user_id: parentId, primary_contact: true });
      setParentId("");
      alert("Parent linked.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="club-assignments">
      <div>
        <div className="font-label">Club</div>
        <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-tight">Assignments</h1>
      </div>

      {/* New assignment */}
      <div className="surface p-4 grid sm:grid-cols-3 gap-2 items-end">
        <div>
          <label className="font-label block mb-1">Player</label>
          <select className="input" value={playerId} onChange={(e) => setPlayerId(e.target.value)} data-testid="assign-player">
            <option value="">Select…</option>
            {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="font-label block mb-1">Coach</label>
          <select className="input" value={coachId} onChange={(e) => setCoachId(e.target.value)} data-testid="assign-coach">
            <option value="">Select…</option>
            {coaches.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.role})</option>)}
          </select>
        </div>
        <button onClick={addAssign} className="btn-primary" data-testid="assign-submit">
          <Plus size={16} weight="bold" /> Assign
        </button>
      </div>

      {/* Parent link */}
      <div className="surface p-4 grid sm:grid-cols-3 gap-2 items-end">
        <div className="sm:col-span-2">
          <label className="font-label block mb-1">Link parent to selected player</label>
          <select className="input" value={parentId} onChange={(e) => setParentId(e.target.value)} data-testid="link-parent">
            <option value="">Select parent…</option>
            {parents.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
          </select>
        </div>
        <button onClick={linkParent} className="btn-secondary" data-testid="link-parent-submit">Link parent</button>
      </div>

      {err && <div className="text-sm text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-3">{err}</div>}

      <div>
        <div className="font-label mb-2">Active assignments</div>
        <div className="grid gap-2">
          {assignments.filter((a) => a.status === "active").map((a) => (
            <div key={a.id} className="surface p-4 flex items-center justify-between" data-testid={`assignment-${a.id}`}>
              <div>
                <span className="font-semibold">{playerMap[a.player_id]?.name || "?"}</span>
                <span className="text-zinc-500"> ← </span>
                <span>{userMap[a.coach_user_id]?.name || "?"}</span>
                <span className="text-xs text-zinc-500"> • {a.type}</span>
              </div>
              <button onClick={() => removeAssign(a.id)} className="btn-ghost text-[#ff0026]" data-testid={`assignment-delete-${a.id}`}>
                <Trash size={16} weight="bold" />
              </button>
            </div>
          ))}
          {assignments.filter((a) => a.status === "active").length === 0 && (
            <div className="text-zinc-500 text-sm">No active assignments.</div>
          )}
        </div>
      </div>
    </div>
  );
}
